import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { Box, Button, IconButton, Stack, Icons, DateTimeStandard } from '@curves/ui-base';
import { GradientColorHeader, ReadTextField, ProductInfo } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { SkuAddToList, InventoryProductList, InputGroup } from "@clubInventory/components/";
import { useInventorySkuAdd } from "@clubInventory/hooks/useSkuAddToList";
import { ExpectedQuantityCell, getExpectedQuantityCellClassName } from "@clubInventory/purchase/components/";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import { getNowTime, parseDate } from "@util/moment";

/**
 * 盤點：此 Dialog 的資料結構以 `countedQuantity` / `stockQuantity` 為唯一來源。
 * 不回退讀取 `quantity` 等其他欄位，避免同一份資料有多個數量欄位造成混淆。
 */
const getCountedQuantity = row => Number(row?.countedQuantity ?? 0);
const getStockQuantity = row => Number(row?.stockQuantity ?? 0);

// 功能：盤點單 API 的 item 映射（productID、variantID、countedQuantity）
const toCountPayloadItem = p => ({
	productID: p.productID,
	variantID: p.variantID ?? 0,
	countQuantity: getCountedQuantity(p),
});

// 功能：盤點清單表格欄位定義（商品資訊、應盤點(盤點/庫存)、刪除）
const productInventoryHeaderRow = [
	{
		cellKey: "productInfo",
		headerLabel: "商品資訊",
		minWidth: 200,
		formatCell: (_, row) => (
			<ProductInfo
				product={{
					name: row.productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
				showImage
			/>
		),
	},
	{
		cellKey: "expectedQuantity",
		headerLabel: "應盤點",
		align: "center",
		minWidth: 100,
		getCellClassName: (_, row) => {
			return getExpectedQuantityCellClassName(getCountedQuantity(row), getStockQuantity(row));
		},
		formatCell: (_, row, { onOpenQuantityEdit }) => {
			return <ExpectedQuantityCell actualQuantity={getCountedQuantity(row)} expectedQuantity={getStockQuantity(row)} item={row} onOpenQuantityEdit={onOpenQuantityEdit} />;
		},
	},
	{
		cellKey: "actions",
		headerLabel: "",
		align: "center",
		minWidth: 80,
		formatCell: (_, row, { onDelete, getProductKey }) => {
			const productKey = getProductKey?.(row) ?? `${row.productID}_${row.variantID ?? ""}`;
			return (
				<IconButton size="small" onClick={() => onDelete?.(productKey)}>
					<Icons.Trash />
				</IconButton>
			);
		},
	},
];

// 功能：商品盤點彈窗（掃碼加入商品、填寫建單人/盤點原因、送出建立盤點單）
const ProductInventoryDialog = ({ open, onClose, refresh }) => {
	const creatorRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();

	const { getClubInventorySearchStockSingleApi, postClubInventoryCountApi } = useClubInventoryApi();

	// 功能：掃碼/輸入 SKU 加入商品，fetchProductBySku 依 SKU 查商品後回傳列表用格式
	const skuAdd = useInventorySkuAdd({
		fetchProductBySku: async sku => {
			const match = await getClubInventorySearchStockSingleApi({ text: sku });
			if (!match) return null;
			return {
				productID: match.productID,
				productName: match.productName,
				variantID: match.variantID,
				variantName: match.variantName,
				sku: match.sku,
				pictureSrc: match.pictureSrc,
				stockQuantity: match.stock,
			};
		},
		getProductKey: p => `${p.productID ?? ""}_${p.variantID ?? ""}`,
	});
	const { products: productList, clearProducts: clearProductList } = skuAdd;

	// 功能：關閉時清空盤點清單並呼叫 onClose
	const handleClose = e => {
		clearProductList();
		isFunction(onClose) && onClose(e);
	};

	const handleSubmit = async () => {
		if (refIsRequiredError(creatorRef)) return;
		if (productList.length === 0) return;
		const hasInvalidQuantity = productList.some(item => {
			const qty = getCountedQuantity(item);
			return qty === undefined || qty === null || qty < 0;
		});
		if (hasInvalidQuantity) return;

		const countDate = getNowTime();
		const payload = {
			employeeID: callRef(creatorRef, "getResult"),
			countDate,
			items: productList.map(toCountPayloadItem),
		};

		const resp = await postClubInventoryCountApi(payload);
		if (resp) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	const orderDateDisplay = parseDate(getNowTime(), DateTimeStandard.DisplayDateBySlash);

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-90rem" }} open={open} onClose={handleClose} fullWidth maxWidth="lg">
			<GradientColorHeader onClose={handleClose}>商品盤點</GradientColorHeader>
			<DialogContent className="p-3">
				<Grid container spacing={3}>
					<Grid item xs={4}>
						<Stack spacing={2} direction="column">
							<ReadTextField label="建單日期">{orderDateDisplay}</ReadTextField>
							<EmployeeSelect label="盤點人" ref={creatorRef} options={employeeOption} fullWidth required />
						</Stack>
					</Grid>
					<Grid item xs={8}>
						<SkuAddToList {...skuAdd}>
							<Stack spacing={2} alignItems="center" className="mb-3">
								<InputGroup className="product-input-group">
									<InputGroup.StartAdornment>
										<SkuAddToList.ScanButton />
									</InputGroup.StartAdornment>
									<InputGroup.Input>
										<SkuAddToList.Input placeholder="請掃描或輸入商品SKU" />
									</InputGroup.Input>
								</InputGroup>
								<SkuAddToList.SubmitButton>加入商品</SkuAddToList.SubmitButton>
							</Stack>
							<SkuAddToList.ScannerDialog title="商品掃描" continuousScan />
							<SkuAddToList.ConfirmDialog />
							<SkuAddToList.List>
								<InventoryProductList headerRow={productInventoryHeaderRow} emptyMessage="請掃描商品條碼加入盤點清單" height="406px" />
							</SkuAddToList.List>
						</SkuAddToList>
					</Grid>
				</Grid>
				<Box className="mt-3 text-right">
					<Button variant="contained" onClick={handleSubmit}>
						新增盤點
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

ProductInventoryDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

ProductInventoryDialog.displayName = "ProductInventoryDialog";

export default ProductInventoryDialog;
